import React, { useState } from "react";
import { Helmet } from "react-helmet";
import { ChevronDown, ChevronUp, HelpCircle } from "lucide-react";

const Navigation = () => {
  return (
    <nav className="dark-header">
      <a href="/">
        <img src="/logos/logo-v5.jpg" alt="ARCANA" className="dark-logo-nav" />
      </a>
      <div className="dark-nav">
        <a href="/" className="dark-nav-link">HOME</a>
        <a href="/#about" className="dark-nav-link">ABOUT US</a>
        <a href="/services" className="dark-nav-link">SERVICES</a>
        <a href="/#pricing" className="dark-nav-link">PRICING</a>
        <a href="/faq" className="dark-nav-link active">FAQ</a>
        <a href="/#assessment" className="dark-nav-link">CONTACT</a>
        <button className="btn-primary" onClick={() => window.location.href = '/#assessment'}>
          FREE ASSESSMENT
        </button>
      </div>
      <button className="mobile-menu-btn">
        <span style={{color: 'var(--oro-arcana)'}}>☰</span>
      </button>
    </nav>
  );
};

const FAQAccordion = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className={`faq-accordion-item ${isOpen ? 'active' : ''}`}>
      <button className="faq-accordion-button" onClick={() => setIsOpen(!isOpen)}>
        <span className="faq-accordion-question">{question}</span>
        {isOpen ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
      </button>
      {isOpen && (
        <div className="faq-accordion-content">
          <p className="faq-answer">{answer}</p>
        </div>
      )}
    </div>
  );
};

const FAQPage = () => {
  const faqCategories = [
    {
      category: "Guarantee & Recovery",
      questions: [
        {
          q: "Do you guarantee recovery?",
          a: "No. Cryptographic systems are mathematically deterministic and do not allow guaranteed outcomes. Arcana Crypto provides a professional technical feasibility assessment based on the data supplied, computational requirements, and case-specific constraints. We communicate realistic probability ranges derived from technical analysis and prior case patterns. Recovery is a structured technical process—not a promise of success."
        },
        {
          q: "What if recovery fails?",
          a: "For standard services, fees cover the agreed technical work performed. For high-complexity or probabilistic cases, base fees reflect operational and computational costs required to execute the process. All pricing structures are explained and agreed in writing before execution begins."
        }
      ]
    },
    {
      category: "Process & Data Handling",
      questions: [
        {
          q: "Do you need my private keys?",
          a: "In most cases, no. We require only the minimum technical data necessary to evaluate feasibility. In specific recovery scenarios, temporary handling of credentials (e.g., seed phrases, passphrases, private keys) may be required strictly for authorized technical execution and only with explicit client consent. Any such data is: Used solely for the agreed technical purpose, Processed in a secure, controlled environment, Not retained beyond the service scope, Deleted upon completion. Arcana Crypto does not permanently store sensitive credentials."
        },
        {
          q: "Can you steal my crypto?",
          a: "No. Arcana Crypto operates strictly on a non-custodial basis. We do not independently execute transactions or take control of client funds. Clients remain in full control of their assets at all times."
        },
        {
          q: "Is my information confidential?",
          a: "Yes. All client information is treated as strictly confidential. Data is used exclusively for service execution and is not shared with third parties unless legally required. Non-Disclosure Agreements (NDAs) are available upon request."
        }
      ]
    },
    {
      category: "Custody & Funds",
      questions: [
        {
          q: "Do you take custody of funds?",
          a: "Never. Arcana Crypto does not manage, hold, or control client assets. All wallet operations remain under the direction and authority of the client."
        }
      ]
    },
    {
      category: "Legality & Ethics",
      questions: [
        {
          q: "Is this legal?",
          a: "Yes. Arcana Crypto provides lawful technical services to rightful asset owners and operates within the legal framework of England and Wales. We engage only in cases that are ethically and legally permissible and reserve the right to decline any engagement where ownership or compliance cannot be reasonably verified."
        },
        {
          q: "Do you work worldwide?",
          a: "Yes. Arcana Crypto operates internationally, subject to applicable legal and regulatory constraints. Clients are responsible for ensuring that the use of our services complies with their local laws."
        }
      ]
    },
    {
      category: "Assessment & Pricing",
      questions: [
        {
          q: "How does the assessment work?",
          a: "Every case begins with a structured technical assessment. During this phase we: Review the technical details, Evaluate feasibility and associated risks, Identify the appropriate service category, Provide a transparent quote and estimated timeline. No work begins without explicit client approval."
        },
        {
          q: "Why are prices fixed if cases differ?",
          a: "Published prices represent standard reference tiers. Final pricing may vary depending on: Technical complexity, Computational requirements, Risk profile, Time and operational scope. Any variation is discussed and agreed before work begins."
        },
        {
          q: "How do payments work?",
          a: "Payment structures depend on the service type: Standard services: fixed pricing, Advanced/high-complexity services: base operational fee + success-based component (where applicable), Emergency services: upfront payment due to immediate allocation of resources. All terms are contractually agreed prior to execution."
        }
      ]
    },
    {
      category: "Service Limitations & Refusals",
      questions: [
        {
          q: "What cases do you refuse?",
          a: "We decline cases when: Ownership cannot be reasonably verified, The probability of success is disproportionately low relative to cost, Legal or ethical concerns are present, The client requests guarantees or illicit actions. Professional integrity and legal compliance guide all engagement decisions."
        },
        {
          q: "Do you provide legal or financial advice?",
          a: "No. Arcana Crypto provides technical recovery and security services only. We do not offer financial, investment, tax, or legal advice."
        }
      ]
    },
    {
      category: "Escrow & Getting Started",
      questions: [
        {
          q: "Can I use escrow?",
          a: "Yes, for selected services and case types. Escrow may be appropriate for: High-value engagements, Medium-to-high complexity recoveries, Situations requiring additional client reassurance. Escrow terms are defined and agreed before execution."
        },
        {
          q: "How do I get started?",
          a: "Contact us via email with a brief description of your situation: legal@arcana-crypto.com. We will guide you through the structured assessment process."
        }
      ]
    }
  ];

  return (
    <div className="App">
      <Helmet>
        <title>FAQ - Crypto Recovery Questions | ARCANA CRYPTO UK</title>
        <meta name="description" content="Frequently asked questions about cryptocurrency recovery services. Pricing, security, process, custody, and legal information. Honest answers." />
        <link rel="canonical" href="https://arcana-crypto.com/faq-seo.html" />
      </Helmet>
      <Navigation />
      
      <section className="section section-alt" style={{marginTop: '128px', minHeight: '100vh'}}>
        <div className="section-container">
          <h1 className="section-title">Frequently Asked Questions</h1>
          <p className="section-subtitle">Everything You Need to Know About Our Services</p>
          
          <div className="section-intro">
            <p>
              We believe in complete transparency. Below you'll find honest answers to common 
              questions about our cryptocurrency recovery and security services.
            </p>
          </div>

          <div style={{maxWidth: '900px', margin: '60px auto 0'}}>
            {faqCategories.map((category, catIndex) => (
              <div key={catIndex} style={{marginBottom: '60px'}}>
                <h2 className="category-title" style={{
                  fontSize: '28px',
                  color: 'var(--oro-arcana)',
                  marginBottom: '32px',
                  paddingBottom: '12px',
                  borderBottom: '2px solid var(--oro-arcana)'
                }}>
                  {category.category}
                </h2>
                
                <div className="faq-accordion">
                  {category.questions.map((faq, qIndex) => (
                    <FAQAccordion 
                      key={qIndex}
                      question={faq.q}
                      answer={faq.a}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Still Have Questions CTA */}
          <div style={{textAlign: 'center', marginTop: '100px', padding: '60px 0', background: 'var(--nero-grigio)', borderTop: '2px solid var(--oro-arcana)'}}>
            <HelpCircle size={64} color="var(--oro-arcana)" style={{margin: '0 auto 24px'}} />
            <h2 className="section-title" style={{fontSize: '36px', marginBottom: '24px'}}>
              Still Have Questions?
            </h2>
            <p className="section-intro" style={{marginBottom: '40px'}}>
              We're here to help. Get in touch for a free consultation.
            </p>
            <a href="/#assessment">
              <button className="btn-primary" style={{fontSize: '18px', padding: '18px 40px'}}>
                CONTACT US
              </button>
            </a>
          </div>
        </div>
      </section>

      <footer className="footer">
        <div className="footer-content">
          <div className="footer-bottom">
            <p className="footer-copyright">
              © 2025-2026 ARCANA CRYPTO LTD - All Rights Reserved
            </p>
            <p style={{fontSize: '13px', color: 'var(--grigio-medio)', marginTop: '12px'}}>
              <a href="mailto:legal@arcana-crypto.com" className="footer-email">
                legal@arcana-crypto.com
              </a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default FAQPage;
